import { Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { AddItemsPickerButton } from "../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilLineGrid, productLabel } from "../FulfilLineGrid";
import {
  SectionHint,
  SectionTitle,
  SelectionPill,
  StepFooter,
  StepOptions,
  StepResult,
  StepState,
} from "../FulfilParts";
import { totalQty, trimQty } from "../fulfilHelpers";
import { useFulfilStep } from "../useFulfilStep";
import { useStepSelection } from "../useStepSelection";

const PICK_LIST_COLUMNS = [
  { key: "line", label: "#", render: row => row.line_number },
  { key: "product", label: "Product", render: row => productLabel(row.product) },
  { key: "warehouse", label: "From", render: row => row.warehouse?.name || "—" },
  { key: "ordered", label: "Ordered", numeric: true, render: row => trimQty(row.quantity) },
  {
    key: "allocated",
    label: "Allocated",
    numeric: true,
    render: row => trimQty(row.allocated_qty),
  },
];

/**
 * Part A: put allocated lines on a pick list for the warehouse.
 *
 * @param {{orderId: (string|number)}} props
 */
const PickListPart = ({ orderId }) => {
  const { query, rows, defaults, run, saving, result, clearResult } = useFulfilStep(
    orderId,
    "pick-list",
  );
  const selection = useStepSelection(rows, { idKey: "line_id" });
  const [release, setRelease] = useState(true);

  useEffect(() => {
    if (defaults.release !== undefined) setRelease(Boolean(defaults.release));
  }, [defaults.release]);

  if (result) {
    return (
      <StepResult
        inline
        result={result}
        nextLabel="Record picks below"
        onNext={clearResult}
        onAgain={clearResult}
      />
    );
  }

  const count = selection.selectedRows.length;

  return (
    <StepState query={query} emptyText="No allocated lines are waiting for a pick list.">
      <FulfilLineGrid
        rows={rows}
        idKey="line_id"
        columns={PICK_LIST_COLUMNS}
        selection={selection}
        disabled={saving}
        ariaLabel="Lines for a pick list"
      />
      <StepOptions>
        <label className="inline">
          <input
            type="checkbox"
            checked={release}
            onChange={event => setRelease(event.target.checked)}
          />
          Release it to the warehouse straight away
        </label>
      </StepOptions>
      <StepFooter inline pill={<SelectionPill selected={count} total={rows.length} />}>
        <AddItemsPickerButton
          type="primary"
          loading={saving}
          disabled={!count || saving}
          onClick={() => run({ line_ids: selection.selectedRows.map(row => row.line_id), release })}
        >
          Create pick list
        </AddItemsPickerButton>
      </StepFooter>
    </StepState>
  );
};

const remaining = row => row.remaining_qty;

/**
 * Part B: record what was picked and where it was put.
 *
 * @param {{orderId: (string|number), onNext: function()}} props
 */
const RecordPicksPart = ({ orderId, onNext }) => {
  const { query, rows, defaults, run, saving, result, clearResult } = useFulfilStep(
    orderId,
    "pick",
  );
  const selection = useStepSelection(rows, {
    idKey: "picklist_line_id",
    defaultQty: remaining,
    maxQty: remaining,
  });
  const [toLocations, setToLocations] = useState({});
  const [markReady, setMarkReady] = useState(true);
  const rowsKey = rows.map(row => row.picklist_line_id).join(",");

  useEffect(() => {
    const defaultsByWarehouse = defaults.default_to_location_id || {};
    setToLocations(
      Object.fromEntries(
        rows.map(row => [row.picklist_line_id, defaultsByWarehouse[row.warehouse_id] ?? null]),
      ),
    );
    if (defaults.mark_ready_to_pack !== undefined)
      setMarkReady(Boolean(defaults.mark_ready_to_pack));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey, defaults]);

  const columns = useMemo(
    () => [
      { key: "picklist", label: "Pick list", render: row => row.picklist?.name || "—" },
      { key: "line", label: "#", render: row => row.line_number },
      { key: "product", label: "Product", render: row => productLabel(row.product) },
      { key: "from", label: "From location", render: row => row.from_location?.name || "—" },
      { key: "toPick", label: "To pick", numeric: true, render: row => trimQty(row.remaining_qty) },
      {
        key: "to",
        label: "Put it in",
        render: (row, { isChecked }) => (
          <LocationSelect
            size="small"
            showSearch
            optionFilterProp="label"
            placeholder="Choose location"
            disabled={!isChecked || saving}
            status={isChecked && !toLocations[row.picklist_line_id] ? "error" : undefined}
            value={toLocations[row.picklist_line_id] ?? undefined}
            onChange={value =>
              setToLocations(previous => ({ ...previous, [row.picklist_line_id]: value }))
            }
            options={(defaults.to_locations?.[row.warehouse_id] || []).map(location => ({
              value: location.id,
              label: location.name,
            }))}
            aria-label={`Put-away location for ${row.product?.number || ""}`}
          />
        ),
      },
    ],
    [defaults.to_locations, saving, toLocations],
  );

  if (result) {
    return (
      <StepResult result={result} nextLabel="Next: Pack" onNext={onNext} onAgain={clearResult} />
    );
  }

  const count = selection.selectedRows.length;
  const missingLocation = selection.selectedRows.some(row => !toLocations[row.picklist_line_id]);
  const total = trimQty(totalQty(selection.checked, selection.quantities));

  const submit = () =>
    run({
      lines: selection.selectedRows.map(row => ({
        picklist_line_id: row.picklist_line_id,
        qty_picked: selection.quantities[row.picklist_line_id],
        to_location_id: toLocations[row.picklist_line_id],
      })),
      mark_ready_to_pack: markReady,
    });

  return (
    <StepState query={query} emptyText="Nothing is waiting to be picked on a released pick list.">
      <FulfilLineGrid
        rows={rows}
        idKey="picklist_line_id"
        columns={columns}
        selection={selection}
        qtyLabel="Picked"
        disabled={saving}
        ariaLabel="Lines to pick"
      />
      <StepOptions>
        <label className="inline">
          <input
            type="checkbox"
            checked={markReady}
            onChange={event => setMarkReady(event.target.checked)}
          />
          Mark fully picked lines ready to pack
        </label>
      </StepOptions>
      {missingLocation ? (
        <MissingNote role="status">Choose where each ticked line goes.</MissingNote>
      ) : null}
      <StepFooter pill={<SelectionPill selected={count} total={rows.length} units={total} />}>
        <AddItemsPickerButton
          type="primary"
          loading={saving}
          disabled={!count || selection.hasErrors || missingLocation || saving}
          onClick={submit}
        >
          Confirm picks
        </AddItemsPickerButton>
      </StepFooter>
    </StepState>
  );
};

/**
 * Step 2: create the pick list, then record the picks.
 *
 * @param {{orderId: (string|number), onNext: function()}} props
 */
export const PickStep = ({ orderId, onNext }) => (
  <>
    <SectionTitle>Create a pick list</SectionTitle>
    <SectionHint>Send the allocated lines to the warehouse to be picked.</SectionHint>
    <PickListPart orderId={orderId} />
    <Divider />
    <SectionTitle>Record picks</SectionTitle>
    <SectionHint>
      Enter what was picked and where it was put. Short picks are fine — the rest stays on the pick
      list.
    </SectionHint>
    <RecordPicksPart orderId={orderId} onNext={onNext} />
  </>
);

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--PEARL, #ecf1f4);
  margin: 24px 0;
`;

const LocationSelect = styled(Select)`
  min-width: 170px;
`;

const MissingNote = styled.p`
  margin: 12px 0 0;
  color: #ad4e00;
  font-size: 13px;
`;
